import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TAuthReduxState } from '~types/redux/state';
import {
    TGetUserDetailsReduxParams,
    TGetUserDetailsReduxResponse,
    TLoginReduxParams,
    TLoginReduxResponse,
    TLogoutReduxResponse,
    TStartupReduxResponse,
} from '~types/feat/auth';

const initialState: TAuthReduxState = {
    loadings: {
        login: false,
        startup: false,
        logout: false,
        getUserDetails: false,
        isMarketOpen: false,
    },
    isMarketOpen: false,
    userDetails: {
        userId: 0,
        email: '',
        authToken: '',
        username: '',
        displayName: '',
        roleName: '',
        branchCode: '',
        access: [],
        allBranchAccess: false,
    },
    loginTime: '',
    logoutTime: '',
    errors: {
        login: '',
        logout: '',
        getUserDetails: '',
        isMarketOpen: '',
    },
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        authClearEntireStore: () => {
            // This reducer does nothing but is used as an action for the store listener to clear the entire redux store.
            // We're not checking for the authLogoutAttempt action type in the store because we want to control when
            // to clear the redux state, instead of clearing it everytime the authLogoutAttempt action is dispatched.
        },
        authClearUserDetails: (state) => {
            state.userDetails = initialState.userDetails;
        },
        authSetAuthToken: (state, action: PayloadAction<string>) => {
            state.userDetails.authToken = action.payload;
        },
        authStartupAttempt: (state) => {
            state.loadings.startup = true;
        },
        authStartupSuccess: (state, action: PayloadAction<TStartupReduxResponse>) => {
            state.loadings.startup = false;
            state.userDetails.authToken = action.payload.authToken;
        },
        authStartupFailure: (state) => {
            state.loadings.startup = false;
        },
        authLoginAttempt: (state, _action: PayloadAction<TLoginReduxParams>) => {
            state.loadings.login = true;
            state.errors.login = '';
        },
        authLoginSuccess: (state, action: PayloadAction<TLoginReduxResponse>) => {
            state.loadings.login = false;
            state.userDetails = action.payload;
        },
        authLoginFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.login = false;
            if (action.payload) state.errors.login = action.payload;
        },
        authResetLoginState: (state) => {
            state.loadings.login = initialState.loadings.login;
            state.errors.login = initialState.errors.login;
        },
        authLogoutAttempt: (state) => {
            state.loadings.logout = true;
            state.errors.logout = '';
        },
        authLogoutSuccess: (state, action: PayloadAction<TLogoutReduxResponse>) => {
            const {
                loginTime,
                logoutTime,
            } = action.payload;

            state.loginTime = loginTime;
            state.logoutTime = logoutTime;

            state.loadings.logout = false;
            state.userDetails = initialState.userDetails;
        },
        authLogoutFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.logout = false;
            if (action.payload) state.errors.logout = action.payload;
        },
        authGetUserDetailsAttempt: (state, _action: PayloadAction<TGetUserDetailsReduxParams>) => {
            state.loadings.getUserDetails = true;
            state.errors.getUserDetails = '';
        },
        authGetUserDetailsSuccess: (state, action: PayloadAction<TGetUserDetailsReduxResponse>) => {
            state.loadings.getUserDetails = false;
            state.userDetails = action.payload;
        },
        authGetUserDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getUserDetails = false;
            if (action.payload) state.errors.getUserDetails = action.payload;
        },
        authGetIsMarketOpenAttempt: (state) => {
            state.loadings.isMarketOpen = true;
            state.errors.isMarketOpen = '';
        },
        authGetIsMarketOpenSuccess: (state, action: PayloadAction<boolean>) => {
            state.loadings.isMarketOpen = false;
            state.isMarketOpen = action.payload;
        },
        authGetIsMarketOpenFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.isMarketOpen = false;
            if (action.payload) state.errors.isMarketOpen = action.payload;
        },
    },
});

export default {
    actions: authSlice.actions,
    reducers: authSlice.reducer,
};
